/**
 * @fileoverview Dialog for importing Meta ad spend from two Ads Manager CSV exports: select → preview → done.
 * @module features/ad-spends/components/import/MetaCsvImportDialog
 */

import { useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toImportErrorInfo } from '../../api/adSpendImportService'
import { useCommitMetaImport, usePreviewMetaImport } from '../../hooks/useMetaCsvImport'
import type { MetaImportResult } from '../../schemas/adSpendImportSchema'
import { ExportInstructions } from './ExportInstructions'
import { ImportFilesDropzone } from './ImportFilesDropzone'
import { ImportPreviewSummary } from './ImportPreviewSummary'

const NEEDS_REVIEW_PATH = '/dashboard/management/products?view=needsReview'
const REQUIRED_FILES = 2

export interface MetaCsvImportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function MetaCsvImportDialog({ open, onOpenChange }: MetaCsvImportDialogProps): ReactElement {
  const { t } = useTranslation('features-ad-spends')
  const navigate = useNavigate()
  const previewMutation = usePreviewMetaImport()
  const commitMutation = useCommitMetaImport()
  const [files, setFiles] = useState<File[]>([])
  const [result, setResult] = useState<MetaImportResult | null>(null)

  const preview = previewMutation.data
  const step = result ? 'done' : preview ? 'preview' : 'select'
  const error = previewMutation.error ?? commitMutation.error
  const errorInfo = error ? toImportErrorInfo(error) : null

  const selectFiles = (next: File[]): void => {
    setFiles(next)
    previewMutation.reset()
    commitMutation.reset()
    if (next.length === REQUIRED_FILES) previewMutation.mutate(next)
  }

  const close = (): void => {
    setFiles([])
    setResult(null)
    previewMutation.reset()
    commitMutation.reset()
    onOpenChange(false)
  }

  const handleImport = (): void => {
    commitMutation.mutate(files, { onSuccess: data => setResult(data) })
  }

  const goToNeedsReview = (): void => {
    close()
    navigate(NEEDS_REVIEW_PATH)
  }

  return (
    <Dialog open={open} onOpenChange={next => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('import.dialog.title', { defaultValue: 'Import Meta spend from CSV' })}</DialogTitle>
          <DialogDescription>
            {t('import.dialog.description', {
              defaultValue: 'Upload the two Ads Manager exports for the same date range. Nothing is saved until you confirm.',
            })}
          </DialogDescription>
        </DialogHeader>

        {step === 'select' && (
          <div className="space-y-3">
            <ImportFilesDropzone files={files} onFilesChange={selectFiles} disabled={previewMutation.isPending} />
            <ExportInstructions />
          </div>
        )}
        {step === 'preview' && preview && <ImportPreviewSummary preview={preview} />}
        {step === 'done' && result && (
          <p className="text-sm">
            {t('import.done.summary', {
              defaultValue: 'Imported {{rows}} rows for {{from}} → {{to}}.',
              rows: result.rowsInserted,
              from: result.dateFrom,
              to: result.dateTo,
            })}
          </p>
        )}

        {previewMutation.isPending && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('import.dialog.checking', { defaultValue: 'Checking files…' })}
          </p>
        )}

        {errorInfo && (
          <div role="alert" className="space-y-1 text-sm text-destructive">
            <p>{errorInfo.message}</p>
            {errorInfo.details.length > 0 && (
              <ul className="list-disc space-y-0.5 pl-5 text-xs">
                {errorInfo.details.map(detail => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'preview' && (
            <>
              <Button type="button" variant="outline" disabled={commitMutation.isPending} onClick={() => selectFiles([])}>
                {t('import.dialog.back', { defaultValue: 'Back' })}
              </Button>
              <Button type="button" disabled={commitMutation.isPending} onClick={handleImport}>
                {commitMutation.isPending
                  ? t('import.dialog.importing', { defaultValue: 'Importing…' })
                  : t('import.dialog.import', { defaultValue: 'Import' })}
              </Button>
            </>
          )}
          {step === 'done' && result && (
            <>
              <Button type="button" variant="outline" onClick={close}>
                {t('import.dialog.close', { defaultValue: 'Close' })}
              </Button>
              {result.unmatchedProductCount > 0 && (
                <Button type="button" onClick={goToNeedsReview}>
                  {t('import.done.review', { defaultValue: 'Review {{n}} products', n: result.unmatchedProductCount })}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
